import { fromJS } from 'immutable';
import React, { useContext, useEffect, useState } from 'react';
import QRCode from 'react-qr-code';
import { EntitiesContext } from '../../../App';
import { useGetSaleInventory, useGetSaleOrders } from '../../../hooks/useSale';

import Loader from '../../Loader';
import ConsignorList from './ConsignorList';

export default function Consignors({ sale }) {
  const entities = useContext(EntitiesContext);
  // selected consignors
  const [filter, setFilter] = useState([]);
  const [sortOrder, setSortOrder] = useState('desc');
  // list of inventory
  const [data, setData] = useState(fromJS([]));

  const metaLists = entities.state.get('list');

  const orders = useGetSaleOrders(sale.id, 1000);
  const inventory = useGetSaleInventory(sale);
  const accounts = entities.state.get('account').filter(a => a.role === 'consigner');

  useEffect(() => {
    if (!inventory.isLoading) {
      setData(entities.state.get('inventory'));
    }
  }, [inventory.isLoading]);

  const isFiltering = () => filter.length >= 1;

  const hasPaidOrder = inventory => {
    return entities.state.get('order').get(inventory.order_id)?.status === 'paid';
  }

  const consignorSelected = inventory => {
    if (!isFiltering()) return false;
    return fromJS(filter).find(f => parseInt(f.get('value')) === parseInt(inventory?.account_id));
  }

  const sortRows = label => {
    setData(data
      .filter(i => isFiltering() ? consignorSelected(i) : true)
      .filter(i => accounts.get(i.account_id))
      .sort((a, b) => sortOrder === 'desc' ? a[label] - b[label] : b[label] - a[label]));

    setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
  }

  const sortStrRows = label => {
    setData(data
      .filter(i => isFiltering() ? consignorSelected(i) : true)
      .filter(i => accounts.get(i.account_id))
      .sort((a, b) => sortOrder === 'desc' ? a[label].localeCompare(b[label]) : b[label].localeCompare(a[label])));

    setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
  }

  if (inventory.isLoading || orders.isLoading) return <Loader />

  return <div className="mt-2">
    <ConsignorList
      consignorSelected={consignorSelected}
      data={data}
      filter={filter}
      setFilter={setFilter}
      isFiltering={isFiltering}
    />

    <br />

    <nav className="level">
      <div className="level-item has-text-centered">
        <div>
          <p className="heading">Total Unsold Items</p>
          <p className="title">
            {data
              .filter(i => !hasPaidOrder(i))
              .filter(i => isFiltering() ? consignorSelected(i) : true)
              .filter(i => accounts.get(i.account_id))
              .count()}
          </p>
        </div>
      </div>
      <div className="level-item has-text-centered">
        <div>
          <p className="heading">Total Value</p>
          <p className="title">
            ${data
              .filter(i => !hasPaidOrder(i))
              .filter(i => isFiltering() ? consignorSelected(i) : true)
              .filter(i => accounts.get(i.account_id))
              .reduce((arr, curr) => arr + curr.price, 0)}
          </p>
        </div>
      </div>
      <div className="level-item has-text-centered">
        <div>
          <p className="heading">PTA Take</p>
          <p className="title">
            ${Math.round(data
              .filter(i => !i.donate && hasPaidOrder(i))
              .filter(i => isFiltering() ? consignorSelected(i) : true)
              .filter(i => accounts.get(i.account_id))
              .reduce((arr, curr) => arr + curr.price, 0) * 0.20)}
          </p>
        </div>
      </div>
      <div className="level-item has-text-centered">
        <div>
          <p className="heading">Donations (Total/Sold)</p>
          <p className="title">
            {data
              .filter(i => isFiltering() ? consignorSelected(i) : true)
              .filter(i => accounts.get(i.account_id))
              .filter(i => i.donate).count()}:
            ${data
              .filter(i => i.donate)
              .filter(i => isFiltering() ? consignorSelected(i) : true)
              .filter(i => accounts.get(i.account_id))
              .reduce((arr, curr) => arr + curr.price, 0)} /
            ${data
              .filter(i => i.order(entities.state.get('order'))?.status === 'paid' && i.donate)
              .filter(i => isFiltering() ? consignorSelected(i) : true)
              .filter(i => accounts.get(i.account_id))
              .reduce((arr, curr) => arr + curr.price, 0)}
          </p>
        </div>
      </div>
      <div className="level-item has-text-centered">
        <div>
          <p className="heading">Total Sold (Paid Only)</p>
          <p className="title">
            {data
              .filter(i => hasPaidOrder(i))
              .filter(i => isFiltering() ? consignorSelected(i) : true)
              .filter(i => accounts.get(i.account_id))
              .count()}:
            ${data
              .filter(i => hasPaidOrder(i))
              .filter(i => isFiltering() ? consignorSelected(i) : true)
              .filter(i => accounts.get(i.account_id))
              .reduce((arr, curr) => arr + curr.price, 0)}
          </p>
        </div>
      </div>
    </nav>

    <table className="table is-fullwidth">
      <thead>
        <tr>
          <th></th>
          <th><a onClick={() => sortRows('id')}>ID</a></th>
          <th>Consignor</th>
          <th>Name</th>
          <th><a onClick={() => sortRows('price')}>Price</a></th>
          <th>20% Off</th>
          <th><a onClick={() => sortStrRows('category')}>Category</a></th>
          <th><a onClick={() => sortStrRows('condition')}>Condition</a></th>
          <th><a onClick={() => sortStrRows('size')}>Size</a></th>
          <th>Kid's Size</th>
          <th>Length</th>
          <th>Event Type</th>
          <th>Boot Type</th>
          <th>Boot Size</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {data
          .filter(i => isFiltering() ? consignorSelected(i) : false)
          .filter(i => accounts.get(i.account_id))
          .valueSeq()
          .map(p => <tr key={p.id}>
            <td><QRCode size={25} value={p.id.toString()} /></td>
            <td>{p.id}</td>
            <td>{entities.state.get('account').get(p.account_id).fullName}</td>
            <td>{p.name}{p.donate && <span> <i>(donation)</i></span>}</td>
            <td>${p.price}</td>
            <td>{p.discounted ? '✓' : ''}</td>
            <td>{metaLists.get(p.category)?.label}</td>
            <td>{metaLists.get(p.condition)?.label}</td>
            <td>{metaLists.get(p.size)?.label}</td>
            <td>{metaLists.get(['kids_size'])?.label}</td>
            <td>{p['length']}</td>
            <td>{metaLists.get(['event_type'])?.label}</td>
            <td>{metaLists.get(p['boot_type'])?.label}</td>
            <td>{metaLists.get(p['boot_size'])?.label}</td>
            <td>{p.order(entities.state.get('order'))?.status === 'paid' ? 'sold' : 'available'}</td>
          </tr>)}
      </tbody>
    </table>
  </div>
}
